import { useState } from "react";

import {
  useUpdateMilestone,
  useUpdateWorkstream,
} from "@/application/use-cases/board/use-board.queries";
import { LANE_FILL_ORDER } from "@/application/use-cases/board/build-implementation-gantt";
import { DELIVERY_STATUS_COLOR, DELIVERY_STATUS_LABEL } from "@/presentation/data/board";
import { useWorkspace } from "@/presentation/providers/workspace.provider";

import type {
  GanttLane,
  GanttMilestoneMarker,
  GanttMonthTick,
  ImplementationGanttViewModel,
} from "@/application/use-cases/board/build-implementation-gantt";

const LABEL_COL = "11rem";

export function ImplementationGantt({ model }: { model: ImplementationGanttViewModel }) {
  // Which milestone diamond is expanded to show its "why" reasoning, and which
  // workstream lane is hovered (reveals its scope + effort). Hooks must run before
  // the early return below, so declare them here unconditionally.
  const [selectedMilestone, setSelectedMilestone] = useState<string | null>(null);
  const [hoveredLane, setHoveredLane] = useState<string | null>(null);

  if (!model.axis) {
    return (
      <p className="text-muted-foreground text-xs italic">
        No milestone dates seeded yet — nothing to place on the calendar.
      </p>
    );
  }

  const selected = model.milestones.find((m) => m.key === selectedMilestone) ?? null;
  const hovered = model.lanes.find((l) => l.key === hoveredLane) ?? null;

  return (
    <div className="glow-card-static p-5">
      <MonthHeader ticks={model.axis.monthTicks} />
      <div className="relative mt-1">
        <MonthGridlines ticks={model.axis.monthTicks} />
        {model.lanes.map((lane) => (
          <LaneRow key={lane.key} lane={lane} onHover={setHoveredLane} />
        ))}
        {model.nowPct !== null ? <NowLine pct={model.nowPct} /> : null}
      </div>
      <StatusLegend />
      <WorkstreamHoverDetail lane={hovered} />
      <MilestoneAxis
        markers={model.milestones}
        selectedKey={selectedMilestone}
        onSelect={(key) => setSelectedMilestone((cur) => (cur === key ? null : key))}
      />
      {selected ? (
        <MilestoneDetail
          key={selected.key}
          marker={selected}
          onClose={() => setSelectedMilestone(null)}
        />
      ) : (
        <p className="text-muted-foreground mt-3 text-[10.5px] italic">
          Click a milestone diamond to see why it earns its place.
        </p>
      )}
      {model.unscheduled.length > 0 ? (
        <p className="text-muted-foreground border-border/60 mt-4 border-t pt-3 text-[11px] leading-relaxed">
          <span className="eyebrow mr-2">Unscheduled</span>
          {model.unscheduled.map((u) => `${u.code} · ${u.title}`).join("  ·  ")}
          <span className="text-muted-foreground/70">
            {" "}
            — no milestone target, off the calendar.
          </span>
        </p>
      ) : null}
    </div>
  );
}

function MonthHeader({ ticks }: { ticks: GanttMonthTick[] }) {
  return (
    <div className="flex" style={{ paddingLeft: LABEL_COL }}>
      <div className="relative h-5 flex-1">
        {ticks.map((tick, i) => (
          <span
            key={`${tick.label}-${i}`}
            className="border-border/70 text-muted-foreground absolute top-0 border-l pl-1.5 font-mono text-[10px] tracking-[0.1em] uppercase"
            style={{ left: `${tick.pct}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Vertical gridlines at the true month positions (same pcts as MonthHeader), drawn
// behind the lane bars. Replaces the old equal-width 12.5% stripe background, which
// didn't line up with the unequal-length months and made a diamond look off-date.
function MonthGridlines({ ticks }: { ticks: GanttMonthTick[] }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
      {ticks.map((tick, i) => (
        <span
          key={`${tick.label}-${i}`}
          className="border-border/40 absolute top-0 bottom-0 border-l"
          style={{ left: `calc(${LABEL_COL} + (100% - ${LABEL_COL}) * ${tick.pct / 100})` }}
        />
      ))}
    </div>
  );
}

function LaneRow({ lane, onHover }: { lane: GanttLane; onHover: (key: string | null) => void }) {
  const { canWrite } = useWorkspace();
  const [editing, setEditing] = useState(false);

  if (editing) {
    return <LaneEditor lane={lane} onClose={() => setEditing(false)} />;
  }

  const label = `${lane.code} · ${lane.title}`;
  return (
    <div
      className="relative z-10 flex items-center"
      style={{ minHeight: "1.75rem" }}
      onMouseEnter={() => onHover(lane.key)}
      onMouseLeave={() => onHover(null)}
    >
      {canWrite ? (
        <button
          type="button"
          onClick={() => {
            onHover(null);
            setEditing(true);
          }}
          title="Edit name / dates"
          className="text-foreground hover:text-primary shrink-0 cursor-pointer truncate pr-3 text-right text-[11px] font-semibold underline-offset-2 hover:underline"
          style={{ width: LABEL_COL }}
        >
          {label}
        </button>
      ) : (
        <div
          className="text-foreground shrink-0 truncate pr-3 text-right text-[11px] font-semibold"
          style={{ width: LABEL_COL }}
        >
          {label}
        </div>
      )}
      <div className="relative flex-1" style={{ height: "1.5rem" }}>
        <LaneBar lane={lane} />
      </div>
    </div>
  );
}

// Inline editor for a workstream lane — name + start/end dates (BF-198, BF-213).
// Shown only to can_write users (RLS also enforces it server-side). ISO date
// strings compare lexicographically, so `end < start` is a valid range check.
function LaneEditor({ lane, onClose }: { lane: GanttLane; onClose: () => void }) {
  const update = useUpdateWorkstream();
  const [name, setName] = useState(lane.title);
  const [start, setStart] = useState(lane.startDate);
  const [end, setEnd] = useState(lane.endDate);
  const invalid = !name.trim() || !start || !end || end < start;

  const save = () => {
    if (invalid) return;
    update.mutate(
      { key: lane.key, patch: { title: name.trim(), start_date: start, end_date: end } },
      { onSuccess: onClose }
    );
  };

  return (
    <div className="flex items-center" style={{ minHeight: "1.75rem" }}>
      <div
        className="text-muted-foreground shrink-0 pr-3 text-right font-mono text-[11px] font-bold"
        style={{ width: LABEL_COL }}
      >
        {lane.code}
      </div>
      <div className="flex flex-1 flex-wrap items-center gap-2 py-0.5">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Workstream name"
          aria-label="Workstream name"
          className="border-border bg-background min-w-40 flex-1 rounded border px-1.5 py-0.5 text-[11px]"
        />
        <input
          type="date"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className="border-border bg-background rounded border px-1.5 py-0.5 text-[11px]"
        />
        <span className="text-muted-foreground text-[11px]">→</span>
        <input
          type="date"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          className="border-border bg-background rounded border px-1.5 py-0.5 text-[11px]"
        />
        <button
          type="button"
          onClick={save}
          disabled={invalid || update.isPending}
          className="text-primary text-[11px] font-semibold disabled:opacity-40"
        >
          {update.isPending ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground text-[11px] hover:underline"
        >
          Cancel
        </button>
        {update.isError && <span className="text-[10px] text-red-500">save failed</span>}
      </div>
    </div>
  );
}

// Hover-reveal panel for a workstream lane — its scope (summary) + effort estimate,
// mirroring the workplan deck's slide-7 stream detail. Reserved height so hovering
// doesn't shift the layout; shows a hint when nothing is hovered.
function WorkstreamHoverDetail({ lane }: { lane: GanttLane | null }) {
  const effort =
    lane && (lane.effortMin !== null || lane.effortMax !== null)
      ? lane.effortMin !== null && lane.effortMax !== null && lane.effortMin !== lane.effortMax
        ? `${lane.effortMin}–${lane.effortMax} pw`
        : `${lane.effortMax ?? lane.effortMin} pw`
      : null;

  return (
    <div className="border-border/60 mt-2 min-h-14 border-t pt-3">
      {lane ? (
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-primary font-mono text-[11px] font-bold">{lane.code}</span>
            <span className="text-foreground text-[12px] font-semibold">{lane.title}</span>
            {effort ? (
              <span className="border-border text-muted-foreground rounded border px-1.5 py-0.5 font-mono text-[9.5px]">
                {effort}
              </span>
            ) : null}
          </div>
          <p className="text-muted-foreground mt-1 text-[11.5px] leading-relaxed">
            {lane.summary ?? "No scope description recorded for this workstream yet."}
          </p>
          <LaneStatusMix lane={lane} />
        </div>
      ) : (
        <p className="text-muted-foreground text-[10.5px] italic">
          Hover a workstream to see its scope and effort.
        </p>
      )}
    </div>
  );
}

/**
 * Screen-reader text for a lane bar: the headline done% plus the status mix behind
 * the colours, since the segmented fill is otherwise purely visual.
 */
function laneProgressLabel(lane: GanttLane): string {
  if (lane.totalCount === 0) {
    return `${lane.code} ${lane.title}: no board features yet.`;
  }
  const mix = lane.segments
    .map((seg) => `${seg.count} ${DELIVERY_STATUS_LABEL[seg.status].toLowerCase()}`)
    .join(", ");
  return `${lane.code} ${lane.title}: ${lane.completionPct}% done — ${mix} of ${lane.totalCount} items.`;
}

/**
 * A lane's bar, filled as a STACKED run of status segments (done → in review →
 * in progress → blocked). The segments sit in an absolutely-positioned flex row so
 * their widths compose without cumulative offset maths, and the wrapper's existing
 * `overflow-hidden rounded-[5px]` clips the ends — so no segment carries a radius of
 * its own. The workstream's own colour stays on the 1px inset outline, which is what
 * still identifies the lane now that the fill encodes status instead.
 */
// The counts behind the hovered lane's segments — the bar shows proportion, this
// shows the actual numbers. Renders nothing for a workstream with no features yet.
function LaneStatusMix({ lane }: { lane: GanttLane }) {
  if (lane.totalCount === 0) {
    return (
      <p className="text-muted-foreground/70 mt-1.5 text-[10.5px] italic">
        No board features on this workstream yet.
      </p>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
      {lane.segments.map((seg) => (
        <span key={seg.status} className="flex items-center gap-1.5 text-[10.5px]">
          <span
            aria-hidden
            className="size-2 rounded-[2px]"
            style={{ background: DELIVERY_STATUS_COLOR[seg.status], opacity: 0.85 }}
          />
          <span className="text-foreground font-medium">{seg.count}</span>
          <span className="text-muted-foreground">{DELIVERY_STATUS_LABEL[seg.status]}</span>
        </span>
      ))}
      <span className="text-muted-foreground/70 font-mono text-[10px]">
        {lane.doneCount}/{lane.totalCount} done
      </span>
    </div>
  );
}

function LaneBar({ lane }: { lane: GanttLane }) {
  return (
    <>
      <div
        role="img"
        aria-label={laneProgressLabel(lane)}
        className="absolute top-1/2 flex -translate-y-1/2 items-center overflow-hidden rounded-[5px] px-2"
        style={{
          left: `${lane.startPct}%`,
          width: `${lane.widthPct}%`,
          height: "16px",
          boxShadow: `inset 0 0 0 1px ${lane.color}`,
        }}
      >
        <span aria-hidden className="absolute inset-0 flex">
          {lane.segments.map((seg) => (
            <span
              key={seg.status}
              // minWidth keeps a 1-of-40 segment from vanishing on a short bar. It
              // costs a pixel or two of width accuracy; the wrapper clips the excess.
              style={{
                width: `${seg.pct}%`,
                minWidth: "2px",
                background: DELIVERY_STATUS_COLOR[seg.status],
                opacity: 0.85,
              }}
            />
          ))}
        </span>
        <span className="text-foreground relative z-10 truncate text-[10px] font-medium">
          {lane.title}
        </span>
      </div>
      <span
        // Done-only, deliberately: a single number blending done + in-review + blocked
        // would read as progress when part of it is a problem. The mix is on hover.
        title={`${lane.doneCount} of ${lane.totalCount} done`}
        className="text-muted-foreground absolute top-1/2 -translate-y-1/2 font-mono text-[9px]"
        style={{ left: `calc(${lane.startPct + lane.widthPct}% + 4px)` }}
      >
        {lane.completionPct}%
      </span>
    </>
  );
}

// Key for the segmented fill. Four colours in a progress bar are unreadable without
// one — and it is what makes red-in-a-progress-bar legible rather than alarming.
function StatusLegend() {
  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5"
      style={{ paddingLeft: LABEL_COL }}
    >
      {LANE_FILL_ORDER.map((status) => (
        <span key={status} className="text-muted-foreground flex items-center gap-1.5 text-[10px]">
          <span
            aria-hidden
            className="size-2 rounded-[2px]"
            style={{ background: DELIVERY_STATUS_COLOR[status], opacity: 0.85 }}
          />
          {DELIVERY_STATUS_LABEL[status]}
        </span>
      ))}
      <span className="text-muted-foreground/70 text-[10px] italic">
        Unfilled = not started · deferred items are out of scope
      </span>
    </div>
  );
}

function NowLine({ pct }: { pct: number }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute top-0 bottom-0 z-20 border-l-2 border-dashed border-[var(--primary)]"
      style={{ left: `calc(${LABEL_COL} + (100% - ${LABEL_COL}) * ${pct / 100})` }}
    >
      <span className="bg-background absolute -top-0.5 left-1.5 px-1 font-mono text-[8.5px] tracking-[0.08em] whitespace-nowrap text-[var(--primary)] uppercase">
        we are here
      </span>
    </span>
  );
}

function MilestoneAxis({
  markers,
  selectedKey,
  onSelect,
}: {
  markers: GanttMilestoneMarker[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="border-border/60 mt-2 flex border-t pt-3">
      <div className="shrink-0" style={{ width: LABEL_COL }} />
      <div className="relative h-20 flex-1">
        {markers.map((m) => (
          <MilestoneMarker
            key={m.key}
            marker={m}
            selected={m.key === selectedKey}
            onSelect={() => onSelect(m.key)}
          />
        ))}
      </div>
    </div>
  );
}

function milestoneTone(marker: GanttMilestoneMarker): string {
  if (marker.reached) return "var(--rag-green)";
  if (marker.isGate) return "var(--rag-red)";
  return "var(--primary)";
}

function MilestoneMarker({
  marker,
  selected,
  onSelect,
}: {
  marker: GanttMilestoneMarker;
  selected: boolean;
  onSelect: () => void;
}) {
  const tone = milestoneTone(marker);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={`${marker.code} · ${marker.title}`}
      className="group absolute top-0 flex w-16 -translate-x-1/2 cursor-pointer flex-col items-center rounded text-center outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-1"
      style={{ left: `${marker.datePct}%` }}
    >
      <span
        aria-hidden
        className="size-3 rotate-45 rounded-[2px] transition-transform group-hover:scale-125"
        style={{
          background: tone,
          boxShadow: selected
            ? `0 0 0 3px var(--background), 0 0 0 5px ${tone}`
            : "0 0 0 3px var(--background)",
        }}
      />
      <span className="mt-1.5 font-mono text-[10px] font-bold" style={{ color: tone }}>
        {marker.code}
      </span>
      <span className="text-muted-foreground mt-0.5 text-[9px] leading-tight">{marker.title}</span>
    </button>
  );
}

function MilestoneDetail({
  marker,
  onClose,
}: {
  marker: GanttMilestoneMarker;
  onClose: () => void;
}) {
  const { canWrite } = useWorkspace();
  const update = useUpdateMilestone();
  const [date, setDate] = useState(marker.date);
  const dirty = !!date && date !== marker.date;
  const tone = milestoneTone(marker);

  const save = () => {
    if (!dirty) return;
    update.mutate({ key: marker.key, patch: { target_date: date } });
  };

  return (
    <div
      className="mt-3 flex items-start gap-3 rounded-md border p-3"
      style={{
        borderColor: tone,
        background: "color-mix(in oklch, var(--background), transparent)",
      }}
    >
      <span
        aria-hidden
        className="mt-0.5 size-2.5 shrink-0 rotate-45 rounded-[2px]"
        style={{ background: tone }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[11px] font-bold" style={{ color: tone }}>
            {marker.code}
          </span>
          <span className="text-foreground text-[12px] font-semibold">{marker.title}</span>
          {marker.isGate ? (
            <span className="text-[9px] font-semibold tracking-wide text-[var(--rag-red)] uppercase">
              hard gate
            </span>
          ) : null}
        </div>
        <p className="text-muted-foreground mt-1 text-[11.5px] leading-relaxed">
          {marker.description ?? "No reasoning recorded for this milestone yet."}
        </p>
        {canWrite ? (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-muted-foreground text-[10px] tracking-wide uppercase">
              Target date
            </span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border-border bg-background rounded border px-1.5 py-0.5 text-[11px]"
            />
            {dirty ? (
              <button
                type="button"
                onClick={save}
                disabled={update.isPending}
                className="text-primary text-[11px] font-semibold disabled:opacity-40"
              >
                {update.isPending ? "Saving…" : "Save date"}
              </button>
            ) : null}
            {update.isError ? <span className="text-[10px] text-red-500">save failed</span> : null}
          </div>
        ) : (
          <p className="text-muted-foreground mt-2 font-mono text-[10.5px]">Target {marker.date}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="text-muted-foreground hover:text-foreground shrink-0 text-[11px]"
        aria-label="Close milestone detail"
      >
        ✕
      </button>
    </div>
  );
}
